import { getAllBrandsClient } from "@/api/saleor/services/indexClient";
import { algoliaRefine, facetAttributeNames, findAlgoliaResponse } from "@/apiUtils/algoliaUtils";
import { algoliaResults } from "@/apiUtils/algoliaUtils/constants";
import { AlgoliaResponse, AlgoliaSearchResult, FilteredFacet } from "@/apiUtils/algoliaUtils/types";
import { hasArrayElements, mapEdgesToItems } from "@/apiUtils/commonUtils";
import { filterIconUrl } from "@/utils/filterIcon";
import { AttributeTypes, facetTypeConstant } from "@/apiUtils/constants";
import {
  Brands,
  FacetConfigurationsMappingType,
  FacetsData,
  PageBreakGridLayout,
  PageBreakPos,
  PopularBrandItem,
} from "@/common/lib/types";

export const fetchAlgoliaData = async (
  productListingFilter: string,
  indexName: string,
  searchParams: Record<string, string>,
  lang: string,
  filteredFacets?: FacetConfigurationsMappingType[],
): Promise<{
  mainResponse: AlgoliaSearchResult;
  allFacetsResponse: AlgoliaSearchResult;
  algoliaResponse: AlgoliaResponse[];
}> => {
  try {
    const searchResultsIn = await algoliaRefine(
      productListingFilter,
      indexName,
      searchParams || {},
      lang,
      undefined,
      true,
      undefined,
      filteredFacets,
    );

    if (!searchResultsIn) {
      throw new Error("No data received from Algolia API");
    }

    return {
      mainResponse: findAlgoliaResponse(searchResultsIn, algoliaResults.MAIN),
      allFacetsResponse: findAlgoliaResponse(searchResultsIn, algoliaResults.ALL_FACETS),
      algoliaResponse: searchResultsIn,
    };
  } catch (error) {
    console.error("Error fetching data from Algolia:", error);
    throw error;
  }
};

export const buildInitialAlgoliaData = (
  mainResponse: AlgoliaSearchResult,
  allFacetsResponse: AlgoliaSearchResult,
  algoliaResponse: AlgoliaResponse[],
  hitsPerPage: number,
) => ({
  products: (mainResponse?.hits as any[]) || [],
  pagination: {
    page: mainResponse?.page || 1,
    totalPages: mainResponse?.nbPages || Math.ceil((mainResponse?.nbHits || 0) / hitsPerPage),
    totalProducts: mainResponse?.nbHits || 0,
  },
  algoliaResponse,
  mainResponse,
  allFacetsResponse,
});

/**
 * Detects if the current page is an accessories page by checking category data from Algolia.
 * Checks both algoliaResponse and facetsData to determine if "Accessories" category exists.
 */
const isAccessoriesPage = (
  facetsData: FacetsData,
  algoliaResponse: AlgoliaResponse[],
): boolean => {
  const categoryAttribute = facetAttributeNames.categoryFilter;
  const categoryDataFromResponse =
    findAlgoliaResponse(algoliaResponse, categoryAttribute)?.facets?.[categoryAttribute] || [];
  const categoryDataFromFacets = facetsData?.[categoryAttribute] || [];
  const categoryData =
    categoryDataFromResponse.length > 0 ? categoryDataFromResponse : categoryDataFromFacets;

  /* An empty category list is not an accessories page: `[].every()` is true. */
  return (
    hasArrayElements(categoryData) &&
    categoryData.every((category) => category.value?.toLowerCase() === "accessories")
  );
};

/** Accessories carry their own material and gender attributes, separate from eyewear's. */
const ACCESSORY_ONLY_ATTRIBUTES = new Set(["acPrdMatEnumCode", "acGenderEnumCode"]);
const EYEWEAR_ONLY_ATTRIBUTES = new Set(["frameMaterialEnum", "genderEnum"]);

/**
 * Whether a filter belongs to the other kind of page than the one being rendered.
 *
 * Material and gender each exist twice — once for eyewear, once for accessories — and the pair
 * carries the same label, so showing both leaves two identical tabs. A category page hides one
 * naturally because its facet comes back empty, but a mixed listing such as search holds both,
 * so the page type decides.
 *
 * Matched on the Akeneo attribute code rather than the mapping title: both gender mappings are
 * titled "Gender", so the title cannot tell them apart.
 */
const shouldExcludeForPageType = (
  attributeCode: string | undefined,
  isAccessoriesPage: boolean,
): boolean => {
  if (!attributeCode) return false;

  return isAccessoriesPage
    ? EYEWEAR_ONLY_ATTRIBUTES.has(attributeCode)
    : ACCESSORY_ONLY_ATTRIBUTES.has(attributeCode);
};

export const extractFilteredFacets = (
  filterConfigurations: FacetConfigurationsMappingType[],
  facetsData: FacetsData,
  algoliaResponse: AlgoliaResponse[],
  enableVirtualTryOn?: boolean,
): FilteredFacet[] => {
  const isAccessories = isAccessoriesPage(facetsData, algoliaResponse);

  const extractedFilters = filterConfigurations.reduce((acc, item) => {
    if (shouldExcludeForPageType(item.attributeCode, isAccessories)) {
      return acc;
    }

    const attributeName = item.facetAttributeEn;

    if (item.isBooleanGroup && Array.isArray(item.booleanMappings)) {
      const booleanMappings = item.booleanMappings;
      const enrichedFacetMapping = booleanMappings
        .map((bm) => {
          const childFacets =
            findAlgoliaResponse(algoliaResponse, bm.facetField)?.facets?.[bm.facetField] ||
            facetsData?.[bm.facetField] ||
            [];
          const trueEntry = Array.isArray(childFacets) ? childFacets.find((f) => f.value === "true") : null;
          if (!trueEntry) return null;
          return {
            ...trueEntry,
            label: bm.label,
            facetField: bm.facetField,
            filterParams: bm.filterParams,
          };
        })
        .filter(Boolean);

      if (enrichedFacetMapping.length > 0) {
        const { mappings, colorMappings, ...rest } = item;
        acc.push({ ...rest, facetMapping: enrichedFacetMapping } as FilteredFacet);
      }
      return acc;
    }

    if (!attributeName && item.type !== facetTypeConstant.PRICE) return acc;

    const facetMapping =
      findAlgoliaResponse(algoliaResponse, attributeName!)?.facets?.[attributeName!] ||
      facetsData?.[attributeName!] ||
      [];

    /* Icons are addressed by attribute and value code, so only an image filter needs them. */
    const iconAttributeCode = item.filterType === "image" ? item.attributeCode : undefined;

    const enrichedFacetMapping = facetMapping.map((facet) => {
      const facetVal = facet.value?.trim().toLowerCase();
      const mappingItem = item.mappings?.find(
        (mapping) => mapping.value?.trim().toLowerCase() === facetVal,
      );
      if (!mappingItem) return facet;

      return {
        ...facet,
        label: mappingItem?.label || facet.label,
        hexCode: mappingItem?.hexCode,
        image: mappingItem?.image,
        imageUrl: iconAttributeCode
          ? (filterIconUrl(iconAttributeCode, mappingItem.code ?? "", mappingItem.mappingRevision) ??
            undefined)
          : undefined,
      };
    });

    const { mappings, colorMappings, ...rest } = item;
    const facetConfigs: FilteredFacet = { ...rest, facetMapping: enrichedFacetMapping };
    
    if (item.showInFilter === false && item.filterItemType === "reference" && enrichedFacetMapping.length <= 1) {
      return acc;
    }
    if (item.type === facetTypeConstant.PRICE) {
      const priceFacet = findAlgoliaResponse(algoliaResponse, facetAttributeNames.price)?.priceData;
      facetConfigs.priceCounts = priceFacet;
    }

    if (
      attributeName === facetAttributeNames.vtoFilter &&
      enableVirtualTryOn &&
      hasArrayElements(facetMapping)
    ) {
      acc.push(facetConfigs);
    } else if (
      attributeName !== facetAttributeNames.vtoFilter &&
      (hasArrayElements(facetMapping) || item.type === facetTypeConstant.PRICE)
    ) {
      acc.push(facetConfigs);
    }

    return acc;
  }, [] as FilteredFacet[]);

  return extractedFilters;
};

export const getPopularBrands = async (brands: PopularBrandItem[]): Promise<PopularBrandItem[]> => {
  try {
    const popularSlugs = brands.reduce((acc, curr) => [...acc, curr?.slug || ""], [] as string[]);
    if (popularSlugs.length > 0) {
      const res: Brands | null = await getAllBrandsClient(popularSlugs.length, {
        slugs: popularSlugs,
      });
      const data = mapEdgesToItems(res) as PopularBrandItem[];
      const fetchedDataMap = new Map(data.map((item) => [item?.slug, item]));
      const orderedBrands = (brands || [])?.map((item) => {
        const fetchedItem = fetchedDataMap.get(item?.slug);
        return fetchedItem ? { ...item, ...fetchedItem } : item;
      });
      return orderedBrands;
    }
    return [];
  } catch (error) {
    return [];
  }
};

export const getPageBreakLayout = ({
  col,
  position,
  row,
  size,
}: PageBreakPos): PageBreakGridLayout => {
  let gridlayout: PageBreakGridLayout = {
    pos: -1,
    colSpan: "",
    mobileHeight: 0,
    desktopHeight: 0,
    mobileWidth: 0,
    desktopWidth: 0,
    mobileAspectRatio: "",
    desktopAspectRatio: "",
  };
  const n = row * col;
  console.log({ col, position, row, size });

  if (position === "right") {
    switch (size) {
      case "single":
        gridlayout = {
          pos: n,
          colSpan: "col-span-1",
          mobileHeight: 294,
          desktopHeight: 621,
          mobileWidth: 348,
          desktopWidth: 318,
          mobileAspectRatio: "0.56/1",
          desktopAspectRatio: "1/1.95",
        };
        break;
      case "halfWidth":
        if (col % 2 === 0) {
          gridlayout = {
            pos: n - Math.floor(col / 2) + 1,
            colSpan: `col-span-${col / 2}`,
            mobileHeight: 294,
            desktopHeight: 425,
            mobileWidth: 348,
            desktopWidth: 660,
            mobileAspectRatio: "0.56/1",
            desktopAspectRatio: "1.55/1",
          };
        } else {
          //odd
          gridlayout = {
            pos: n - Math.floor(col / 2),
            colSpan: `col-span-${Math.ceil(col / 2)}`,
            mobileHeight: 294,
            desktopHeight: 425,
            mobileWidth: 348,
            desktopWidth: 660,
            mobileAspectRatio: "0.56/1",
            desktopAspectRatio: "1.55/1",
          };
        }
        break;
      case "fullWidth":
        gridlayout = {
          pos: n - col + 1,
          colSpan: `col-span-${col}`,
          mobileHeight: 440,
          desktopHeight: 492,
          mobileWidth: 709,
          desktopWidth: 1344,
          mobileAspectRatio: "0.85/1",
          desktopAspectRatio: "2.73/1",
        };
    }
  } else {
    switch (size) {
      case "single":
        gridlayout = {
          pos: n - col + 1,
          colSpan: "col-span-1",
          mobileHeight: 294,
          desktopHeight: 621,
          mobileWidth: 348,
          desktopWidth: 318,
          mobileAspectRatio: "0.56/1",
          desktopAspectRatio: "1/1.95",
        };
        break;
      case "halfWidth":
        if (col % 2 == 0) {
          //even
          gridlayout = {
            pos: n - col + 1,
            colSpan: `col-span-${col / 2}`,
            mobileHeight: 294,
            desktopHeight: 425,
            mobileWidth: 348,
            desktopWidth: 660,
            mobileAspectRatio: "0.56/1",
            desktopAspectRatio: "1.55/1",
          };
        } //odd
        else {
          gridlayout = {
            pos: n - Math.ceil(col / 2),
            colSpan: `col-span-${Math.ceil(col / 2)}`,
            mobileHeight: 294,
            desktopHeight: 425,
            mobileWidth: 348,
            desktopWidth: 660,
            mobileAspectRatio: "0.56/1",
            desktopAspectRatio: "1.55/1",
          };
        }
        break;
      case "fullWidth":
        gridlayout = {
          pos: n - col + 1,
          colSpan: `col-span-${col}`,
          mobileHeight: 440,
          desktopHeight: 492,
          mobileWidth: 709,
          desktopWidth: 1344,
          mobileAspectRatio: "0.85/1",
          desktopAspectRatio: "2.73/1",
        };
    }
  }

  return gridlayout;
};
